import { userState } from '../../../stores/user';
import { postToApp } from '../../../lib/rnBridge';
import { isInAppWebView } from '../../../analytics/appInfo';

const imageBase = import.meta.env.VITE_IMG_BASE_URL;

/** 客服热线：展示文案 + 拨号号码（统一配置，业务侧勿硬编码） */
export type ServicePhone = {
  /** 界面展示，如 `+86 1328000000` */
  display: string;
  /** 拨号用（无空格），如 `+861328000000` */
  dial: string;
};

export const SERVICE_PHONES: ServicePhone[] = [
  { display: '(852) 25613008', dial: '+85225613008' },
  // { display: '+86 1328000000', dial: '+861328000000' },
];

/** 去掉空格，得到可拨号号码 */
export function toDialNumber(phone: string) {
  return String(phone || '').replace(/\s+/g, '');
}

/**
 * App：发 WEB_TO_APP_CallPhone
 * H5/PC：不在此跳转（由 PhoneLink 的 `<a href="tel:">` 处理；按钮等非 a 场景可再传 tel 兜底）
 */
export function callPhone(phone: string) {
  const dial = toDialNumber(phone);
  if (!dial) return;

  if (isInAppWebView()) {
    postToApp({
      type: 'WEB_TO_APP_CallPhone',
      payload: { phone: dial },
    });
  }
}

/**
 * 获取图片URL
 * @param path 图片路径
 * @returns 图片URL
 */
export const getImageUrl = (path: string) => {
  return imageBase + path;
}

/**
 * 跳转到登录页
 */
export const gotoLogin = () => {
  userState.showLoginPage = true;
}

// 获取当前年份
export const getYear = () => {
  return new Date().getFullYear();
}


/**
 * 数组分组函数
 * @param {Array} arr - 原始数组
 * @param {number} size - 每组元素个数
 * @returns {Array} 分组后的数组
 */
export function groupArray(arr, size) {
  const result = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}


/**
 * 截断保留指定位数小数（不四舍五入）
 * @param {number | string} num 原始数字
 * @param {number} decimal 保留小数位数，默认2
 * @returns {number}
 */
export function toFixedTrunc(num, decimal = 2) {

  const n = Number(num);
  if (isNaN(n)) return 0;

  const p = Math.pow(10, decimal);
  return Math.trunc(n * p) / p;
}


/**
 * 数字格式化：大于等于1亿转亿，否则转万，截断2位小数
 * @param {number|string} num 原始数字
 * @returns {string} 格式化后字符串
 */
export function formatMoney(num) {
  const n = Number(num);
  if (isNaN(n)) return '0.00';

  const yi = 100000000;
  const wan = 10000;

  // 大于等于 1 亿
  if (n >= yi) {
    const val = Math.trunc((n / yi) * 100) / 100;
    return `${val}亿`;
  }

  // 小于 1 亿，转万
  const val = Math.trunc((n / wan) * 100) / 100;
  return `${val}万`;
}


/**
 *  防抖函数
 */
export function debounce(func, delay: number) {
  let timer = null;
  return function () {
    const context = this;
    const args = arguments;
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      func.apply(context, args);
    }, delay);
  };
}

/**
 * 节流函数
 */
export function throttle(func, interval: number) {
  let lastTime = 0; // 上次执行时间
  let timer = null;

  return function (...args: any[]) {
    const context = this;
    const now = Date.now(); // 当前时间

    // 如果是第一次执行，直接触发
    if (!lastTime) {
      func.apply(context, args);
      lastTime = now;
      return;
    }

    // 清除之前的延迟执行定时器
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    // 计算距离下次可以执行的剩余时间
    const remainingTime = interval - (now - lastTime);

    if (remainingTime <= 0) {
      // 剩余时间不足，立即执行
      func.apply(context, args);
      lastTime = now;
    } else {
      // 剩余时间充足，延迟执行最后一次触发
      timer = setTimeout(() => {
        func.apply(context, args);
        lastTime = Date.now();
        timer = null;
      }, remainingTime);
    }
  };
}

/**
 * 判断是否可以加载下一页
 * @param total 总条数
 * @param pageNum 当前页
 * @param pageSize 每页条数
 * @returns boolean true = 可以加载下一页
 */
export function hasNextPage(
  total: number,
  pageNum: number,
  pageSize: number
): boolean {
  // 处理无效值
  if (total <= 0 || pageNum <= 0 || pageSize <= 0) return false;

  // 当前已经加载到最后一页 = 没有下一页
  return pageNum * pageSize < total;
}


/**
 * 给文章内容中的图片添加 referrerpolicy="no-referrer"
 * @param htmlStr 文章内容
 * @returns 添加 referrerpolicy="no-referrer" 后的文章内容
 */
export function addReferrerForImg(htmlStr: string) {
  return htmlStr.replace(/<img([^>]*?)>/gi, '<img$1 referrerpolicy="no-referrer">');
}